import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import PollingData from 'core/components/PollingData'
import Info from 'core/components/validatedForm/Info'
import Button from 'core/elements/button/Button'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import {
  enableMaintenanceMode,
  getVmsForHost,
  getVmsWithTag,
  listHypervisors,
  listResmgrHosts,
} from 'openstack/components/infrastructure/actions'
import { listVirtualMachines } from 'openstack/components/vms/actions'
import { getMigrationsSince, listMigrations } from 'openstack/components/vms/migrations/actions'
import { vmMigrationsSelector } from 'openstack/components/vms/migrations/selectors'
import DisableMaintenanceDialog from './DisableMaintenanceDialog'
import MigrationProgressModal from './MigrationProgressModal'
import RetryMigrationsDialog from './RetryMigrationsDialog'

// Nova migration statuses - includes both live migration and cold migration (resize/migrate) statuses
// Live migration: accepted -> queued -> preparing -> running -> post-migrating -> completed/failed
// Cold migration: pre-migrating -> migrating -> post-migrating -> finished -> confirming -> confirmed OR error -> reverted
export const pendingStatuses = [
  'accepted',
  'queued',
  'preparing',
  'running',
  'pre-migrating', // Cold migration initial state
  'migrating', // Cold migration in-progress
  'post-migrating', // Live/cold migration cleanup phase
  'confirming', // Cold migration confirming
]
export const completedStatuses = [
  'completed',
  'finished',
  'confirmed', // Cold migration confirmed successfully
]
export const failedStatuses = [
  'failed',
  'error',
  'cancelled', // Migration was cancelled
  'reverted', // Migration was reverted (failure scenario)
]

const dateFormat = 'ddd D MMM h:mm A'

export default function MaintenanceModeStatusBar({ maintenanceId, host }) {
  const classes = useStyles()
  const [migratingVms, setMigratingVms] = useState([])
  const [loadingVms, setLoadingVms] = useState(true)
  const [latestMigrationEnd, setLatestMigrationEnd] = useState(null)
  const [showProgressDialog, setShowProgressDialog] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [showRetryDialog, setShowRetryDialog] = useState(false)
  const [errorFetchingVms, setErrorFetchingVms] = useState(false)
  const tag = maintenanceId?.split(':')?.[0]
  const timestamp = maintenanceId?.split(':')?.[1]
  const startTime = useMemo(() => {
    if (!timestamp) {
      return null
    }
    return moment(Number(timestamp)).format(dateFormat)
  }, [timestamp])

  // const { reload: reloadVms } = useListAction(listVirtualMachines, {})
  const { loading: loadingMigrations, reload: reloadMigrations } = useListAction(listMigrations)
  const { update: pollMigrations } = useUpdateAction(getMigrationsSince)

  const migrations = useSelector(vmMigrationsSelector)

  const reloadTagVms = useCallback(() => {
    if (!tag || !host?.id) {
      return
    }
    const makeCalls = async () => {
      try {
        setLoadingVms(true)
        const vms = await getVmsWithTag(tag)
        const vmsOnHost = await getVmsForHost(host?.id)
        const vmIds = vms?.map((vm) => vm.id)
        const untaggedVms = vmsOnHost?.filter((vm) => !vmIds.includes(vm.id))
        setMigratingVms([...vms, ...untaggedVms])
        setErrorFetchingVms(false)
      } catch (e) {
        setErrorFetchingVms(true)
      } finally {
        setLoadingVms(false)
      }
    }
    makeCalls()
  }, [tag, host.id])

  useEffect(() => {
    // Get initial number of VMs on the host to determine
    // if migrations are necessary
    reloadTagVms()
    reloadMigrations(true, true)
  }, [])

  const vmsWithMigrations = useMemo(() => {
    if (migratingVms?.length) {
      const migratingVmIds = migratingVms?.reduce(
        (accum, vm) => ({
          ...accum,
          [vm.id]: true,
        }),
        {},
      )
      let latestAnyMigrationTime = ''
      const migrationsByVmId = migrations.reduce((accum, migration) => {
        const createdUnix = moment(migration?.createdTimestamp).format('x')
        // Do not check migrations older than maintenance mode trigger
        // or migrations for other VMs
        if (createdUnix < timestamp || !migratingVmIds[migration?.instance_uuid]) {
          return accum
        }

        // Only want latest a VM's latest migration
        if (
          accum[migration?.instance_uuid] &&
          accum[migration?.instance_uuid].createdTimestamp > migration?.createdTimestamp
        ) {
          return accum
        }

        // Record latest migration update
        if (migration?.updatedTimestamp > latestAnyMigrationTime) {
          latestAnyMigrationTime = migration.updatedTimestamp
        }

        return {
          ...accum,
          [migration?.instance_uuid]: migration,
        }
      }, {})
      if (latestAnyMigrationTime) {
        const formattedTime = moment(latestAnyMigrationTime).format(dateFormat)
        setLatestMigrationEnd(formattedTime)
      }
      return migratingVms.map((vm) => ({
        ...vm,
        migration: migrationsByVmId[vm.id],
      }))
    }
    return []
  }, [migratingVms, migrations, timestamp])

  const migrationStatus = useMemo(() => {
    const skippedVms = vmsWithMigrations
      .filter((vm) => !vm?.migration)
      .map((vm) => ({
        ...vm,
        migrationStatus: 'skipped',
      }))
    const pendingVms = vmsWithMigrations
      .filter((vm) => pendingStatuses.includes(vm?.migration?.status))
      .map((vm) => ({
        ...vm,
        migrationStatus: 'pending',
      }))
    const completedVms = vmsWithMigrations
      .filter((vm) => completedStatuses.includes(vm?.migration?.status))
      .map((vm) => ({
        ...vm,
        migrationStatus: 'success',
      }))
    const failedVms = vmsWithMigrations
      .filter((vm) => failedStatuses.includes(vm?.migration?.status))
      .map((vm) => ({
        ...vm,
        migrationStatus: 'failed',
      }))
    // Priority pending > failed > skipped > success
    const overallStatus = pendingVms.length
      ? 'pending'
      : failedVms.length
        ? 'failed'
        : skippedVms.length
          ? 'skipped'
          : vmsWithMigrations.length === completedVms.length
            ? 'success'
            : 'success'
    return {
      status: overallStatus,
      skippedVms,
      pendingVms,
      completedVms,
      failedVms,
    }
  }, [vmsWithMigrations])

  const loading = loadingVms || loadingMigrations

  return (
    <>
      <PollingData
        hidden
        loading={loadingVms}
        // @ts-ignore
        onReload={() => {
          pollMigrations()
        }}
        refreshDuration={1000 * 15}
        pause={migrationStatus?.status === 'success'}
      />
      {showProgressDialog && (
        <MigrationProgressModal
          host={host}
          onClose={() => setShowProgressDialog(false)}
          migrationStatus={migrationStatus}
          startTime={startTime}
          endTime={latestMigrationEnd}
          vmTag={tag}
          refreshResourcesFn={() => {
            pollMigrations()
            reloadTagVms()
          }}
        ></MigrationProgressModal>
      )}
      {showDisableDialog && (
        <DisableMaintenanceDialog
          rows={[host]}
          onClose={() => setShowDisableDialog(false)}
          onDetailsPage
        />
      )}
      {showRetryDialog && (
        <RetryMigrationsDialog host={host} onClose={() => setShowRetryDialog(false)} tag={tag} />
      )}
      {loading ? (
        <div className={classes.spaceBelow}>
          <Info className={classes.success}>
            <Text variant="body2">Loading...</Text>
          </Info>
        </div>
      ) : errorFetchingVms ? (
        <div className={classes.spaceBelow}>
          <Info className={classes.error}>
            <div className={classes.errorRetrieving}>
              <FontAwesomeIcon className={classes.errorIcon} solid>
                circle-xmark
              </FontAwesomeIcon>
              <Text variant="body2">Error fetching maintenance details.</Text>
              <Button
                onClick={() => {
                  reloadTagVms()
                  reloadMigrations(true, true)
                }}
                variant="secondary"
              >
                Try Again
              </Button>
            </div>
          </Info>
        </div>
      ) : (
        <div className={classes.spaceBelow}>
          {migrationStatus.status === 'pending' && (
            <Info className={classes.active}>
              <div className={classes.statusBar}>
                <div className={classes.statusBarInfo}>
                  <FontAwesomeIcon className={classes.activeIcon} solid spin>
                    circle-notch
                  </FontAwesomeIcon>
                  <Text variant="subtitle2">Migrations are currently in progress...</Text>
                  <div></div>
                  <div className={classes.statusBarProperties}>
                    <Text variant="body2">
                      Number of VMs:{' '}
                      <b>
                        {migrationStatus?.completedVms?.length} of {vmsWithMigrations?.length} VMs
                      </b>
                    </Text>
                    <Text variant="body2">
                      Started: <b>{startTime}</b>
                    </Text>
                  </div>
                </div>
                <div className={classes.statusBarButtons}>
                  <Button variant="secondary" onClick={() => setShowProgressDialog(true)}>
                    See Details
                  </Button>
                </div>
              </div>
            </Info>
          )}
          {migrationStatus.status === 'success' && (
            <Info className={classes.success}>
              <div className={classes.statusBar}>
                <div className={classes.statusBarInfo}>
                  <FontAwesomeIcon className={classes.successIcon} solid>
                    circle-check
                  </FontAwesomeIcon>
                  <Text variant="subtitle2">All VMs migrated successfully!</Text>
                  <div></div>
                  <div className={classes.statusBarProperties}>
                    <Text variant="body2">
                      Number of VMs:{' '}
                      <b>
                        {migrationStatus?.completedVms?.length} of {vmsWithMigrations?.length} VMs
                      </b>
                    </Text>
                    <Text variant="body2">
                      Started: <b>{startTime}</b>
                    </Text>
                    <Text variant="body2">
                      Completed: <b>{latestMigrationEnd}</b>
                    </Text>
                  </div>
                </div>
                <div className={classes.statusBarButtons}>
                  <Button variant="secondary" onClick={() => setShowProgressDialog(true)}>
                    See Details
                  </Button>
                  <Button variant="secondary" onClick={() => setShowDisableDialog(true)}>
                    Disable Maintenance Mode
                  </Button>
                </div>
              </div>
            </Info>
          )}
          {migrationStatus.status === 'skipped' && (
            <Info className={classes.warning}>
              <div className={classes.statusBar}>
                <div className={classes.statusBarInfo}>
                  <FontAwesomeIcon className={classes.warningIcon} solid>
                    circle-exclamation
                  </FontAwesomeIcon>
                  <Text variant="subtitle2">Some VMs were skipped during migration!</Text>
                  <div></div>
                  <div className={classes.statusBarProperties}>
                    <Text variant="body2">
                      Migrated VMs:{' '}
                      <b>
                        {migrationStatus?.completedVms?.length} of {vmsWithMigrations?.length}
                      </b>
                    </Text>
                    <Text variant="body2">
                      Skipped VMs:{' '}
                      <b>
                        {migrationStatus?.skippedVms?.length} of {vmsWithMigrations?.length}
                      </b>
                    </Text>
                  </div>
                  <div></div>
                  <Text variant="body2">
                    <b>Note:</b> See details for information on VMs skipped
                  </Text>
                </div>
                <div className={classes.statusBarButtons}>
                  <Button variant="secondary" onClick={() => setShowProgressDialog(true)}>
                    See Details
                  </Button>
                  <Button variant="secondary" onClick={() => setShowDisableDialog(true)}>
                    Disable Maintenance Mode
                  </Button>
                  <Button
                    variant="secondary"
                    icon="arrows-rotate"
                    onClick={() => setShowRetryDialog(true)}
                  >
                    Retry Migrations
                  </Button>
                </div>
              </div>
            </Info>
          )}
          {migrationStatus.status === 'failed' && (
            <Info className={classes.error}>
              <div className={classes.statusBar}>
                <div className={classes.statusBarInfo}>
                  <FontAwesomeIcon className={classes.errorIcon} solid>
                    circle-xmark
                  </FontAwesomeIcon>
                  <Text variant="subtitle2">One or more errors occurred during migration!</Text>
                  <div></div>
                  <div className={classes.statusBarProperties}>
                    <Text variant="body2">
                      Migrated VMs:{' '}
                      <b>
                        {migrationStatus?.completedVms?.length} of {vmsWithMigrations?.length}
                      </b>
                    </Text>
                    <Text variant="body2">
                      Skipped VMs:{' '}
                      <b>
                        {migrationStatus?.skippedVms?.length} of {vmsWithMigrations?.length}
                      </b>
                    </Text>
                    <Text variant="body2">
                      Failed VMs:{' '}
                      <b>
                        {migrationStatus?.failedVms?.length} of {vmsWithMigrations?.length}
                      </b>
                    </Text>
                  </div>
                  <div></div>
                  <Text variant="body2">
                    <b>Error:</b> Some VMs failed to migrate.
                  </Text>
                </div>
                <div className={classes.statusBarButtons}>
                  <Button variant="secondary" onClick={() => setShowProgressDialog(true)}>
                    See Details
                  </Button>
                  <Button variant="secondary" onClick={() => setShowDisableDialog(true)}>
                    Disable Maintenance Mode
                  </Button>
                  <Button
                    variant="secondary"
                    icon="arrows-rotate"
                    onClick={() => setShowRetryDialog(true)}
                  >
                    Retry Migrations
                  </Button>
                </div>
              </div>
            </Info>
          )}
        </div>
      )}
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  statusBar: {
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
  },
  statusBarInfo: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    alignItems: 'center',
    gap: 12,
  },
  statusBarProperties: {
    display: 'flex',
    gap: 8,
  },
  statusBarButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  active: {
    background: theme.palette.blue[100],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  success: {
    background: theme.palette.green[100],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  warning: {
    background: theme.palette.yellow[100],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  error: {
    background: theme.palette.red[100],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  activeIcon: {
    color: theme.palette.blue[700],
    fontSize: 24,
  },
  successIcon: {
    color: theme.palette.green['700-opaque'],
    fontSize: 24,
  },
  warningIcon: {
    color: theme.palette.yellow[700],
    fontSize: 24,
  },
  errorIcon: {
    color: theme.palette.red[700],
    fontSize: 24,
  },
  spaceBelow: {
    marginBottom: 16,
  },
  errorRetrieving: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: 'max-content max-content max-content',
    gap: 16,
  },
}))
